import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Chip, CircularProgress, Skeleton, Stack, Paper } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import PageTitle from '../../../components/PageTitle';
import TatTopBar from './components/TatTopBar';
import TatStepper from './components/TatStepper';
import TatDashboard from './components/TatDashboard';
import StageHeader from './components/StageHeader';
import StageFooter from './components/StageFooter';
import HelpDrawer from './components/HelpDrawer';
import CpCodeViewerDrawer from './components/CpCodeViewerDrawer';
import Stage1Continuation from './stages/Stage1_Continuation';
import StageProfile from './stages/StageProfile';
import Stage2Extract from './stages/Stage2_Extract';
import Stage3Review from './stages/Stage3_Review';
import Stage4Submit from './stages/Stage4_Submit';
import Stage5Done from './stages/Stage5_Done';
import { STAGES, validateAll } from './constants';
import { nextStage, prevStage, startSetup, startUpdate, updateContextFromMerchant } from '../../../redux/tatWizardSlice';
import {
  useLazyGetTatMerchantContextQuery,
  useLazyGetTatConfigQuery,
  useGetTatStandardQuery,
} from '../../../redux/tatApiSlice';
import { useLazyGetMerchantQuery, useGetServiceabilityGroupsQuery } from '../../../redux/commonApiCalls';

export default function MerchantTatWizard({ mode = 'onboard' }) {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { merchantOid } = useParams();

  const stage = useSelector((s) => s.tatWizard.stage);
  const tat = useSelector((s) => s.tatWizard.tat);
  const tatBlended = useSelector((s) => s.tatWizard.tatBlended);
  const modeByST = useSelector((s) => s.tatWizard.modeByST);
  const uploadResults = useSelector((s) => s.tatWizard.uploadResults);
  const flowMode = useSelector((s) => s.tatWizard.flowMode);
  const extractionState = useSelector((s) => s.tatWizard.agreement?.extractionState);
  const agreementConfirmed = useSelector((s) => s.tatWizard.agreement?.confirmed);
  const selectedSTs = useSelector((s) => s.tatWizard.selectedSTs);

  const [helpOpen, setHelpOpen] = useState(false);
  const [contextLoading, setContextLoading] = useState(false);

  const [getTatMerchantContext] = useLazyGetTatMerchantContextQuery();
  const [getTatConfig] = useLazyGetTatConfigQuery();
  const [getMerchantDetail] = useLazyGetMerchantQuery();

  // Fetch standard TAT on mount — populates standardTat in Redux for deviation hints
  useGetTatStandardQuery();

  // When merchantOid is in the URL, initialise the flow.
  // Order matters:
  //   1. startSetup/startUpdate resets stale data immediately.
  //   2. Both APIs are called in parallel:
  //      a. GET /api/tat/merchants/{oid}  → initFromContext (via onQueryStarted)
  //      b. GET /api/merchant/{oid}       → updateContextFromMerchant (fallback)
  //   3. updateContextFromMerchant fills in any fields the TAT API didn't return
  //      (name, resolvedServiceTypes, freightDirections).
  useEffect(() => {
    if (!merchantOid) return;
    if (mode === 'update') {
      dispatch(startUpdate({ oid: merchantOid, name: '' }));
    } else {
      dispatch(startSetup({ oid: merchantOid, name: '' }));
    }
    setContextLoading(true);
    Promise.all([
      getTatMerchantContext(merchantOid),
      getMerchantDetail({ id: merchantOid }),
    ]).then(([, merchantResult]) => {
      // Fallback: enrich with merchant detail data where the TAT API had gaps
      const merchantData = merchantResult?.data?.data ?? merchantResult?.data;
      if (merchantData) dispatch(updateContextFromMerchant(merchantData));
    }).finally(() => setContextLoading(false));
    // Always attempt to load existing TAT config so the grid is PRE-POPULATED whenever
    // values already exist — not only via the explicit update route. GET /config returns
    // null on 404 (first-time setup), so this is a harmless no-op for brand-new merchants.
    // When config exists it also seeds the tatOriginal snapshot that powers the
    // warn-before-overwrite dialog on publish.
    getTatConfig(merchantOid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [merchantOid, mode]);

  const profilePending = stage === 1 && selectedSTs.length === 0;
  // AI extraction removed — gate only on user confirming the TAT data via the preview modal.
  const extractionPending = stage === 2 && !agreementConfirmed;

  const { data: groups = [] } = useGetServiceabilityGroupsQuery();
  const issues = useMemo(
    () => validateAll(tat, tatBlended, modeByST, selectedSTs, groups),
    [tat, tatBlended, modeByST, selectedSTs, groups],
  );

  const handleNext = () => {
    if (profilePending) {
      enqueueSnackbar('Select at least one freight direction and mode × size to resolve service types', { variant: 'warning' });
      return;
    }
    if (extractionPending) {
      enqueueSnackbar('Click "Preview & Confirm" to review and confirm the TAT values before continuing', { variant: 'warning' });
      return;
    }
    if (stage === 3 && issues.length > 0) {
      enqueueSnackbar(`${issues.length} validation issue(s) — fix them before submitting`, { variant: 'error' });
      return;
    }
    if (stage === 4) {
      if (uploadResults.length === 0) {
        enqueueSnackbar('Publish at least once before finishing', { variant: 'warning' });
        return;
      }
      if (uploadResults.some((r) => r.status === 'error')) {
        enqueueSnackbar(`${uploadResults.filter((r) => r.status === 'error').length} failure(s) outstanding — fix before finishing`, { variant: 'warning' });
        return;
      }
    }
    dispatch(nextStage());
  };

  const handleBack = () => dispatch(prevStage());

  const renderStage = () => {
    switch (stage) {
      case 0: return <Stage1Continuation />;
      case 1: return <StageProfile />;
      case 2: return <Stage2Extract />;
      case 3: return <Stage3Review />;
      case 4: return <Stage4Submit />;
      case 5: return <Stage5Done />;
      default: return null;
    }
  };

  const nextLabel = (() => {
    if (stage === 1) return 'Continue to Upload TATs';
    if (stage === 2) return 'Review & Validate';
    if (stage === 3) return 'Submit & Upload';
    return null;
  })();

  const nextDisabled = profilePending
    || extractionPending
    || (stage === 3 && issues.length > 0)
    || (stage === 4 && (uploadResults.length === 0 || uploadResults.some((r) => r.status === 'error')));

  const hint = profilePending
    ? 'Resolve at least one service type to continue'
    : extractionPending
      ? 'Click "Preview & Confirm" to review and confirm TAT values'
      : stage === 3 && issues.length > 0
        ? `${issues.length} validation issue(s) blocking submit`
        : '';

  const footerVisible = stage === 1 || stage === 2 || stage === 3;

  // No merchant selected (e.g. browser back from /tat-setup/:id to /tat-setup).
  // Both routes render this component — show the dashboard listing when no OID in URL.
  if (!merchantOid) return <TatDashboard />;

  if (contextLoading) {
    return (
      <Box>
        {/* Stepper skeleton */}
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 3 }}>
          {[1, 2, 3, 4, 5].map((i) => (
            <Stack key={i} direction="row" alignItems="center" spacing={1}>
              <Skeleton variant="circular" width={28} height={28} />
              <Skeleton variant="text" width={70} height={16} />
              {i < 5 && <Skeleton variant="rectangular" width={32} height={2} sx={{ borderRadius: 1 }} />}
            </Stack>
          ))}
        </Stack>
        {/* Content card skeleton */}
        <Paper variant="outlined" sx={{ p: 2.5 }}>
          <Skeleton variant="text" width="35%" height={28} sx={{ mb: 1 }} />
          <Skeleton variant="text" width="60%" height={16} sx={{ mb: 2.5 }} />
          <Stack spacing={1.5}>
            {[100, 80, 90, 70].map((w, i) => (
              <Stack key={i} direction="row" spacing={1.5} alignItems="center">
                <Skeleton variant="circular" width={18} height={18} />
                <Skeleton variant="rectangular" width={`${w}%`} height={14} sx={{ borderRadius: 1 }} />
              </Stack>
            ))}
          </Stack>
          <Box sx={{ mt: 3 }}>
            <Skeleton variant="rectangular" width="100%" height={120} sx={{ borderRadius: 1.5, mb: 1.5 }} />
            <Skeleton variant="rectangular" width="100%" height={80} sx={{ borderRadius: 1.5 }} />
          </Box>
        </Paper>
        {/* Footer skeleton */}
        <Stack direction="row" justifyContent="flex-end" spacing={1.5} sx={{ mt: 2 }}>
          <Skeleton variant="rectangular" width={90} height={36} sx={{ borderRadius: 1 }} />
          <Skeleton variant="rectangular" width={120} height={36} sx={{ borderRadius: 1 }} />
        </Stack>
      </Box>
    );
  }

  return (
    <>
      <PageTitle>
        {mode === 'update' ? 'Update Merchant TAT' : 'Merchant TAT Setup'}
      </PageTitle>
      <Box>
        {mode === 'update' && (
          <Box sx={{ px: 2.5, pt: 2, pb: 1.5 }}>
            <Chip
              size="small"
              label="Update Mode · editing TAT for an existing merchant"
              color="warning"
              variant="outlined"
              sx={{ fontWeight: 600 }}
            />
          </Box>
        )}
        <TatTopBar onOpenHelp={() => setHelpOpen(true)} />

        {flowMode === null ? (
          <TatDashboard defaultFilter={mode === 'update' ? 'live' : 'all'} />
        ) : (
          <>
            <TatStepper />
            <Box
              sx={{
                maxWidth: 1640,
                mx: 'auto',
                px: 2.5,
                pt: 2.25,
                pb: 11,
                display: 'flex',
                flexDirection: 'column',
                gap: 1.75,
              }}
            >
              {stage !== 5 && (
                <StageHeader stage={stage} onOpenHelp={() => setHelpOpen(true)} />
              )}
              <Box
                sx={{
                  bgcolor: 'background.paper',
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 2,
                  boxShadow: (t) => t.customShadows?.z1 || 1,
                  p: 2.5,
                }}
              >
                {renderStage()}
              </Box>
              {footerVisible && (
                <StageFooter
                  stage={stage}
                  totalStages={STAGES.length}
                  onBack={handleBack}
                  onNext={handleNext}
                  nextLabel={nextLabel}
                  nextDisabled={nextDisabled}
                  hint={hint}
                />
              )}
            </Box>
            <CpCodeViewerDrawer />
          </>
        )}

        <HelpDrawer open={helpOpen} onClose={() => setHelpOpen(false)} />
      </Box>
    </>
  );
}
